<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { toast } from "@/common/utils/toast";

import { planList } from "@/common/api/rest/plan";
import { userAccountByMember } from "@/common/api/rest/user";

import type { PlanType } from "@/common/types/plan/plan";
import type { PlanPriceType } from "@/common/types/plan/price";

import { DefaultTheme } from "@/common/themes";
import { baseSelect, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();

const account = ref<any>(null);
const plans = ref<PlanType[]>([]);
const isLoading = ref(false);
const frequency = ref("MONTHLY");

const featuredPlan = ref("6a93450233dbe0acac0d7a8b");

const frequencyOptions = computed(() => [
  { option: "Mensal", value: "MONTHLY" },
  // { option: "Semestral", value: "SEMESTER" },
  { option: "Anual", value: "YEARLY" },
]);

const currencyOptions = computed(() => ({
  BRL: "R$",
  USD: "$",
  EUR: "€",
}));

async function fecthAccount() {
  await userAccountByMember(store.getters.user?.member_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        account.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchPlans() {
  try {
    isLoading.value = true;
    const accountId = account.value?.account_id;
    if (!accountId) {
      toast({ message: t("error.no_account"), type: "error" });
      return;
    }

    const response = await planList(accountId);
    if (response.success) {
      plans.value = response.data.list;
    } else {
      toast({ message: response.message, type: "error" });
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  } finally {
    isLoading.value = false;
  }
}

function selectPlan(plan: PlanType) {
  router.push(`/checkout/${plan.plan_id}?frequency=${frequency.value}`);
}

function getCurrency(plan: PlanType) {
  const price = plan.prices.find((item) => item.frequency === frequency.value);

  if (price) {
    return currencyOptions.value[price.currency];
  }

  return " -- ";
}

function getPrice(plan: PlanType) {
  const price = plan.prices.find((item) => item.frequency === frequency.value);

  if (price) {
    return (price.price / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return " -- ";
}

function getDescription(plan: PlanType) {
  const price = plan.prices.find((item) => item.frequency === frequency.value);

  if (price) {
    return price.description;
  }

  return " -- ";
}

onMounted(async () => {
  await fecthAccount();
  if (account.value) {
    fetchPlans();
  }
});
</script>

<template>
  <default-theme menu-active="plans" :account-status="account?.status">
    <section class="plans-page">
      <div class="plans-header">
        <h1 class="header-title">{{ t("plan.texts.choose_plan") }}</h1>
        <p class="header-subtitle">{{ t("plan.texts.subtitle") }}</p>

        <div class="header-frequency">
          <base-select
            v-model="frequency"
            :value="frequency"
            :options="frequencyOptions"
            :is-empty="false"
          >
          </base-select>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <span>{{ t("common.loading") }}</span>
      </div>

      <div v-else class="plans-container">
        <div
          v-for="plan in plans"
          :key="plan.plan_id"
          class="plan-card"
          :class="{ 'is-featured': plan.plan_id === featuredPlan }"
        >
          <div v-if="plan.plan_id === featuredPlan" class="featured-badge">
            {{ t("plan.labels.most_popular") }}
          </div>

          <div class="plan-content">
            <div class="plan-top">
              <h2 class="plan-name">{{ plan.name }}</h2>
              <p class="plan-description">
                {{ plan.description || "O plano ideal para você." }}
              </p>

              <div class="plan-price">
                <span class="currency">
                  {{ getCurrency(plan) }}
                </span>
                <span class="amount">
                  {{ getPrice(plan) }}
                </span>
              </div>

              <p class="price-description">
                {{ getDescription(plan) }}
              </p>
            </div>

            <div class="plan-divider"></div>

            <ul class="plan-features">
              <template v-for="mod in plan.modules" :key="mod.module_id">
                <li>
                  <svg-icon icon="confirmed" size="1.2rem" />
                  <div class="description">
                    <strong>{{ mod.name }}</strong>
                    <span v-html="mod.description"></span>
                  </div>
                </li>
              </template>
            </ul>
          </div>

          <div class="plan-footer">
            <button class="subscribe-btn" @click="selectPlan(plan)">
              Comece Agora
            </button>
          </div>
        </div>
      </div>
    </section>
  </default-theme>
</template>

<style lang="scss" scoped>
// ==========================================
// Variáveis do Tema
// ==========================================
$color-primary: #3b82f6;
$color-primary-hover: #2563eb;
$color-text-main: #0f172a;
$color-text-muted: #64748b;
$color-bg-card: #fff;
$color-bg-hover: #f8fafc;
$color-border: #e2e8f0;
$color-border-hover: #cbd5e1;

$shadow-sm:
  0 4px 6px -1px rgba(0, 0, 0, 0.05),
  0 2px 4px -1px rgba(0, 0, 0, 0.03);
$shadow-md:
  0 20px 25px -5px rgba(0, 0, 0, 0.1),
  0 10px 10px -5px rgba(0, 0, 0, 0.04);
$shadow-primary:
  0 20px 25px -5px rgba($color-primary, 0.15),
  0 10px 10px -5px rgba($color-primary, 0.05);

.price-description {
  font-size: 0.875rem;
  color: $color-text-muted;
}

// ==========================================
// Layout e Estrutura Principal
// ==========================================
.plans {
  &-page {
    min-height: 100vh;
    padding-block: 2rem 4rem;
    padding-inline: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: transparent;
  }

  &-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 5rem;

    // Aninhamento para as classes .header-icon, .header-title, etc.
    .header {
      &-title {
        font-size: 1.75rem;
        font-weight: 800;
        color: $color-text-main;
        margin: 0;
      }

      &-subtitle {
        font-size: 1.125rem;
        color: $color-text-muted;
        margin-top: 0.75rem;
        max-width: 500px;
      }

      &-frequency {
        width: 10rem;
        margin-top: 1rem;
      }
    }
  }

  &-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    justify-content: center;
    max-width: 1050px;
    width: 100%;
    padding: 0 1rem;
  }
}

// ==========================================
// Componente: Plan Card
// ==========================================
.plan {
  &-card {
    position: relative;
    background: $color-bg-card;
    border-radius: 24px;
    border: 1px solid $color-border;
    box-shadow: $shadow-sm;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    height: 100%;

    &:hover {
      box-shadow: $shadow-md;
    }

    &.is-featured {
      border: 2px solid $color-primary;
      box-shadow: $shadow-primary;

      @media (min-width: 1024px) {
        transform: scale(1.05);
        z-index: 10;
      }
    }
  }

  &-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &-top {
    text-align: left;
  }

  &-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: $color-text-main;
    margin: 0 0 0.5rem 0;
  }

  &-description {
    color: $color-text-muted;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0 0 1.5rem 0;
    min-height: 2.85rem;
  }

  &-price {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    margin-bottom: 1.5rem;

    .currency {
      font-size: 1rem;
      font-weight: 600;
      color: $color-text-muted;
    }

    .amount {
      font-size: 3rem;
      font-weight: 800;
      color: $color-text-main;
      line-height: 1;
      letter-spacing: -0.02em;
    }

    .period {
      color: $color-text-muted;
      font-size: 1rem;
      font-weight: 500;
    }
  }

  &-divider {
    height: 1px;
    background-color: $color-border;
    width: 100%;
    margin-bottom: 2rem;
  }

  &-features {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    flex: 1;

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.5rem 0;
      color: #334155;
      font-size: 0.95rem;
      line-height: 1.4;

      svg {
        color: $color-primary;
        flex-shrink: 0;
        margin-top: 0.1rem;
      }
    }
  }

  &-footer {
    margin-top: auto;
  }
}

// ==========================================
// Elementos Avulsos (Badge, Button, Loading)
// ==========================================
.featured-badge {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: $color-primary;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 1.25rem;
  border-radius: 9999px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 4px 6px -1px rgba($color-primary, 0.3);
}

.subscribe-btn {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid $color-border;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: $color-bg-card;
  color: $color-text-main;

  &:hover {
    background: $color-bg-hover;
    border-color: $color-border-hover;
  }

  // Estilo do botão quando o pai (.plan-card) possui a classe .is-featured
  .is-featured & {
    background: $color-primary;
    border-color: $color-primary;
    color: #fff;

    &:hover {
      background: $color-primary-hover;
      border-color: $color-primary-hover;
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem;
  color: $color-text-muted;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid $color-border;
    border-top-color: $color-primary;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
